import React from 'react';
import { ResetLocalOverridingStyles } from 'components/RunPayroll/ComplianceActions/styles';
import {
  Box,
  Button,
  CashIcon,
  RefreshIcon,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
} from '@razorpay/blade/components';
import { getSalaryStructureComponentFormulas } from 'components/SalaryStructures/utils';
import { UseQueryResult } from 'react-query';
import { AppError } from 'utils/AppError';
import ComponentHeader from 'components/PayDetails/components/generics/ComponentHeader';
import { GetSalaryStructureGraphQLResponse } from 'api/gql-utils/salaryStructures/types';
import { getStaticMediaUrl } from 'utils/Urls';
import failedToLoad from 'assets/network-error.svg';
import { parseGraphqlErrorFromExtensions } from 'utils/Api';

export const SalaryStrctureComponents = ({
  salaryStructureQueryResponse,
  openModifyStructureModal,
}: {
  salaryStructureQueryResponse: UseQueryResult<
    GetSalaryStructureGraphQLResponse['payrollGetSalaryStructure']['data']
  >;
  openModifyStructureModal: () => void;
}) => {
  const { isLoading, isFetching, error, data, refetch } = salaryStructureQueryResponse;

  const errorMessage = error ? parseGraphqlErrorFromExtensions(error as AppError) : null;

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        height="300px"
        padding="spacing.6">
        <img
          width="100px"
          height="100px"
          src={getStaticMediaUrl(failedToLoad)}
          alt="Failed to load"
        />
        <Text size="medium" color="surface.text.gray.muted" marginTop="spacing.4">
          {'Uh Oh, Failed to load :('}
        </Text>
        {errorMessage ? (
          <Text size="medium" color="surface.text.gray.muted" marginTop="spacing.2">
            {errorMessage}
          </Text>
        ) : null}
        <Button
          marginTop="spacing.4"
          onClick={refetch}
          variant="secondary"
          icon={RefreshIcon}
          iconPosition="left"
          size="small">
          Refetch Default Salary Structure
        </Button>
      </Box>
    );
  }

  return (
    <>
      <Box
        marginBottom="spacing.4"
        display="flex"
        justifyContent="space-between"
        alignItems="center">
        <ComponentHeader
          icon={<CashIcon size="medium" color="surface.icon.onSea.onSubtle" />}
          iconBackground={'surface.background.sea.subtle'}
          title={'Fixed'}
        />
        <Button variant="secondary" onClick={openModifyStructureModal} isLoading={isFetching}>
          Modify default structure
        </Button>
      </Box>
      <Box marginBottom="spacing.6">
        <Table
          data={{
            nodes: data?.salaryStructureComponents ?? [],
          }}
          isLoading={isLoading}
          isRefreshing={isFetching}>
          {(tableData) => (
            <>
              <TableHeader>
                <TableHeaderRow>
                  <TableHeaderCell>Component</TableHeaderCell>
                  <TableHeaderCell>Calculation</TableHeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {tableData.map((component: any) => {
                  const { id, salaryComponent } = component;

                  return (
                    <TableRow key={id} item={component}>
                      <TableCell>
                        <Text weight="regular" color="surface.text.gray.normal">
                          {salaryComponent.name}
                        </Text>
                      </TableCell>

                      <TableCell>
                        <Text weight="medium" color="surface.text.gray.muted" variant="caption">
                          {getSalaryStructureComponentFormulas(component)}
                        </Text>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </>
          )}
        </Table>
      </Box>
    </>
  );
};
